import { ADMIN_URL } from './handbookUrls';
import type { Block, HandbookDocument, HandbookGroupId, HandbookPageDef } from './handbookTypes';
import { stepAnchor } from './inlineMarkup';

/** Where a page lives on the Handbook site: /<group>/<slug>. */
export function pagePath(page: Pick<HandbookPageDef, 'group' | 'slug'>): string {
  return `/${page.group}/${page.slug}`;
}

export function findPage(handbook: HandbookDocument, group: string | undefined, slug: string | undefined): HandbookPageDef | undefined {
  return handbook.pages.find((page) => page.group === group && page.slug === slug);
}

export function pagesInGroup(handbook: HandbookDocument, group: HandbookGroupId): HandbookPageDef[] {
  return handbook.pages.filter((page) => page.group === group);
}

export function neighbours(handbook: HandbookDocument, page: HandbookPageDef): { previous?: HandbookPageDef; next?: HandbookPageDef } {
  const index = handbook.pages.indexOf(page);
  return { previous: handbook.pages[index - 1], next: handbook.pages[index + 1] };
}

/** Table-of-contents entries: every h2, and every top-level step on step-by-step pages. */
export function headings(page: HandbookPageDef): Array<{ id: string; text: string }> {
  return page.blocks.flatMap((block) => {
    if (block.t === 'h2') return [{ id: block.id, text: block.text }];
    if (block.t === 'steps') return block.items.map((item, index) => ({ id: stepAnchor(index, item.title), text: `${index + 1}. ${item.title}` }));
    return [];
  });
}

/**
 * Where a link written in the content goes. Content links name pages as
 * `/handbook/<group>/<slug>`, which on this site is `/<group>/<slug>`; any
 * other path is a Studio Admin page; anything else leaves for the web.
 */
export function resolveHandbookHref(href: string): { internal: true; to: string } | { internal: false; href: string } {
  if (href === '/handbook') return { internal: true, to: '/' };
  if (href.startsWith('/handbook/') || href.startsWith('/handbook#')) return { internal: true, to: href.slice('/handbook'.length) };
  if (href.startsWith('/')) return { internal: false, href: `${ADMIN_URL.replace(/\/$/, '')}${href}` };
  return { internal: false, href };
}

function blockText(block: Block): string[] {
  switch (block.t) {
    case 'p':
    case 'h2':
    case 'h3':
      return [block.text];
    case 'list':
      return block.items;
    case 'code':
      return [block.title ?? '', block.code];
    case 'callout':
      return [block.title, block.text];
    case 'steps':
      return block.items.flatMap((item) => [item.title, ...item.blocks.flatMap(blockText)]);
    case 'table':
      return [...block.head, ...block.rows.flat()];
    case 'diagram':
      return [block.caption ?? ''];
    case 'cards':
      return block.items.flatMap((item) => [item.title, item.text]);
    case 'definitions':
      return block.items.flatMap((item) => [item.term, item.text]);
  }
}

export type HandbookSearchHit = { page: HandbookPageDef; score: number; snippet?: string };

/** Title and summary matches rank above body matches; every term has to appear somewhere. */
export function searchHandbook(handbook: HandbookDocument, query: string): HandbookSearchHit[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  const hits: HandbookSearchHit[] = [];
  for (const page of handbook.pages) {
    const head = `${page.title} ${page.summary}`.toLowerCase();
    const bodyParts = page.blocks.flatMap(blockText);
    const body = bodyParts.join(' ').toLowerCase();
    if (!terms.every((term) => head.includes(term) || body.includes(term))) continue;
    const score = terms.reduce((total, term) => total + (page.title.toLowerCase().includes(term) ? 10 : 0) + (head.includes(term) ? 4 : 0) + (body.includes(term) ? 1 : 0), 0);
    const snippetSource = bodyParts.find((part) => terms.some((term) => part.toLowerCase().includes(term)));
    hits.push({ page, score, snippet: snippetSource?.replace(/[`*]/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').slice(0, 160) });
  }
  return hits.sort((a, b) => b.score - a.score);
}
